<template>
  <div>
    <div class="sidebar-container" :class="{ 'sidebar-collapsed': collapsed }" v-if="this.$route.meta.showSidebar">
      <div id="sidebar">
        <div class="user-info">
          <div class="user-greeting">
            <div class="greeting-content" :class="{ 'greeting-content--collapsed': collapsed }">
              <div class="greeting-profile" :class="{ 'greeting-profile--collapsed': collapsed }">
                <user-avatar :user="user" size="md" clickable />
                <div class="greeting-texts" :class="{ 'greeting-texts--collapsed': collapsed }">
                  <div class="greeting-text">Olá, {{ userName }}</div>
                  <div class="welcome-message">Bem-vindo ao Configurador</div>
                </div>
              </div>
              <div class="user-actions" v-if="!collapsed">
                <button @click="logout" class="logout-btn" title="Sair">
                  <i class="fas fa-sign-out-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div id="nav-menu" class="links">
          <sidebar-nav-group
            group-id="inicio"
            title="Acesso rápido"
            :collapsed="collapsed"
            :route-names="['home']"
          >
            <navigation-icon :collapsed="collapsed" destination="home" label="Home" />
            <navigation-action
              nav-id="pesquisa"
              label="Pesquisa"
              title="Pesquisa (Ctrl+P)"
              :collapsed="collapsed"
              @click="openQuickSearch"
            />
          </sidebar-nav-group>

          <sidebar-nav-group
            group-id="cadastros"
            title="Campanhas"
            :collapsed="collapsed"
            :show-divider-when-collapsed="true"
            :route-names="['customers', 'templates', 'settings', 'landingPages', 'uploadFile']"
          >
            <navigation-icon v-if="canAccessClientes" :collapsed="collapsed" destination="customers" label="Clientes" />
            <navigation-icon v-if="canAccessTemplates" :collapsed="collapsed" destination="templates" label="Templates" />
            <navigation-icon :collapsed="collapsed" destination="settings" label="Configurações" />
            <navigation-icon :collapsed="collapsed" destination="landingPages" label="Landing Pages" />
            <navigation-icon :collapsed="collapsed" destination="uploadFile" label="Upload" />
          </sidebar-nav-group>

          <sidebar-nav-group
            group-id="banco-dados"
            title="Banco de dados"
            :collapsed="collapsed"
            :show-divider-when-collapsed="true"
            :route-names="['dump', 'limpeza']"
          >
            <navigation-icon :collapsed="collapsed" destination="dump" label="Dump" />
            <navigation-icon :collapsed="collapsed" destination="limpeza" label="Limpeza" />
            <navigation-action
              v-if="canAccessClientes"
              nav-id="inicializar-indices"
              label="Inicializar índices"
              :collapsed="collapsed"
              @click="openInicializarSistema"
            />
          </sidebar-nav-group>

          <sidebar-nav-group
            v-if="canAccessUsuarios || canAccessSupervisao"
            group-id="administracao"
            title="Administração"
            :collapsed="collapsed"
            :show-divider-when-collapsed="true"
            :route-names="['usuario', 'usuarioNovo', 'usuarioEditar', 'usuarioVisualizar', 'oauthApps', 'oauthAppNovo', 'oauthAppEditar', 'oauthIntegracaoTutorial', 'oauthIntegracaoTutorialTipo', 'supervisao']"
          >
            <navigation-icon v-if="canAccessUsuarios" :collapsed="collapsed" destination="usuario" label="Usuários" />
            <navigation-icon v-if="canAccessUsuarios" :collapsed="collapsed" destination="oauthApps" label="Apps e Atalhos" />
            <navigation-icon v-if="canAccessSupervisao" :collapsed="collapsed" destination="supervisao" label="Supervisão" />
          </sidebar-nav-group>

          <sidebar-nav-group
            group-id="relatorios"
            title="Relatórios"
            :collapsed="collapsed"
            :show-divider-when-collapsed="true"
            :route-names="['custosVariaveis']"
          >
            <navigation-icon :collapsed="collapsed" destination="custosVariaveis" label="Custos Variáveis" />
          </sidebar-nav-group>
        </div>

        <div class="sidebar-footer" :class="{ 'sidebar-footer--collapsed': collapsed }">
          <button
            type="button"
            class="collapse-toggle"
            @click="$emit('toggle-collapse')"
            :aria-label="collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'"
          >
            <span
              class="collapse-toggle__state collapse-toggle__state--expanded"
              :class="{ 'collapse-toggle__state--hidden': collapsed }"
            >
              <i class="fas fa-chevron-left collapse-toggle__icon collapse-toggle__icon--left" aria-hidden="true" />
              <span>Recolher menu</span>
            </span>
            <span
              class="collapse-toggle__state collapse-toggle__state--collapsed"
              :class="{ 'collapse-toggle__state--hidden': !collapsed }"
              aria-hidden="true"
            >
              <i class="fas fa-chevron-right collapse-toggle__icon collapse-toggle__icon--solo" aria-hidden="true" />
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { canAccessClientes, canAccessTemplates, canAccessUsuarios, canAccessSupervisao } from '@/lib/Permissions'
import { resetAllLoginData } from '../../../lib/Authentication/src/VerifyTokenJWT'
import { initializeAppTheme } from '@/lib/appThemes'
import SidebarNavGroup from './SidebarNavGroup.vue'
import NavigationAction from './NavigationAction.vue'
import UserAvatar from '@/components/layout/UserAvatar.vue'
import { getUserDisplayName } from '@/lib/userProfile'

export default {
  name: 'sidebar',
  components: {
    SidebarNavGroup,
    NavigationAction,
    UserAvatar,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      userPermissions: 'autenticacao/getUserPermissions',
      user: 'autenticacao/getUser',
      supervisionados: 'supervisao/getSupervisionados',
    }),
    userName() {
      return getUserDisplayName(this.user)
    },
    canAccessClientes() {
      if (!this.userPermissions) return false
      return canAccessClientes(this.userPermissions)
    },
    canAccessTemplates() {
      if (!this.userPermissions) return false
      return canAccessTemplates(this.userPermissions)
    },
    canAccessUsuarios() {
      if (!this.userPermissions) return false
      return canAccessUsuarios(this.userPermissions)
    },
    canAccessSupervisao() {
      if (!this.userPermissions) return false
      return canAccessSupervisao(this.userPermissions, this.supervisionados)
    }
  },
  data() {
    return {
      hoverQuickSearch: false,
    }
  },
  mounted() {
    initializeAppTheme();
    this.$store.dispatch('supervisao/fetchSupervisionados');
  },
  methods: {
    logout() {
      resetAllLoginData();
      window.location.reload()
    },
    openQuickSearch() {
      this.$root.$emit('open-quicksearch');
    },
    openInicializarSistema() {
      this.$root.$emit('open-inicializar-sistema');
    },
    onQuickSearchHover(val) {
      this.hoverQuickSearch = val;
    },
  }
}
</script>

<style scoped>
.collapse-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.4rem;
  width: 100%;
  padding: var(--spacing-xs) 0;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 400;
  letter-spacing: 0.2px;
  cursor: pointer;
}

.collapse-toggle:hover {
  color: var(--text-secondary);
}

.collapse-toggle:hover .collapse-toggle__icon--left {
  transform: translateX(-2px);
}

.collapse-toggle:hover .collapse-toggle__icon--solo {
  transform: translateX(2px);
}

.collapse-toggle__state {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  transition:
    opacity 0.22s ease,
    transform 0.28s var(--sidebar-transition-easing);
}

.collapse-toggle__state--hidden {
  position: absolute;
  opacity: 0;
  transform: scale(0.94);
  pointer-events: none;
}

.collapse-toggle__state--expanded:not(.collapse-toggle__state--hidden) {
  transition-delay: 0.12s;
}

.collapse-toggle__state--collapsed:not(.collapse-toggle__state--hidden) {
  transition-delay: 0.1s;
}

.collapse-toggle__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.85rem;
  font-size: 0.62rem;
  line-height: 1;
  opacity: 0.85;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.collapse-toggle__icon--solo {
  width: auto;
  font-size: 0.72rem;
  opacity: 0.65;
}

.collapse-toggle:hover .collapse-toggle__icon {
  opacity: 1;
}

.collapse-toggle__state--collapsed {
  padding: 2px 0;
}

.sidebar-footer {
  flex-shrink: 0;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-xs);
  margin-top: auto;
  border-top: 1px solid var(--border-sidebar);
  display: flex;
  justify-content: center;
}

.sidebar-footer--collapsed .collapse-toggle__icon--solo {
  opacity: 0.65;
}

.sidebar-collapsed .links {
  align-items: center !important;
}

.sidebar-collapsed .nav-link {
  justify-content: center;
  padding: 2px 0 !important;
  min-height: 30px;
}

.greeting-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 0;
  transition: gap var(--sidebar-transition-duration) var(--sidebar-transition-easing);
}

.greeting-content--collapsed {
  justify-content: center;
  align-items: center;
}

.greeting-profile {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
  min-width: 0;
  transition: gap var(--sidebar-transition-duration) var(--sidebar-transition-easing);
}

.greeting-profile--collapsed {
  flex: 0 0 auto;
  width: auto;
  justify-content: center;
  gap: 0;
}

.sidebar-collapsed .user-greeting {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 0;
  padding: var(--spacing-xs) 0;
  flex-shrink: 0;
}

.sidebar-collapsed .user-info {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  padding-right: 0;
}

.user-greeting {
  margin-top: var(--spacing-xs);
  padding: var(--spacing-xs) 0;
  flex-shrink: 0;
  width: 100%;
}

.user-greeting:hover {
  transition: all 0.2s ease;
}

.greeting-texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  max-width: 12rem;
  text-align: left;
  overflow: hidden;
  opacity: 1;
  transition:
    opacity 0.22s ease,
    max-width var(--sidebar-transition-duration) var(--sidebar-transition-easing),
    flex var(--sidebar-transition-duration) var(--sidebar-transition-easing);
}

.greeting-texts--collapsed {
  flex: 0;
  max-width: 0;
  opacity: 0;
  transition-delay: 0s;
}

.greeting-texts:not(.greeting-texts--collapsed) {
  transition-delay: 0.14s;
}

.greeting-text {
  color: var(--text-primary);
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  word-wrap: break-word;
  font-family: var(--font-family);
  margin: 0;
  margin-bottom: 2px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.welcome-message {
  color: var(--text-muted);
  font-size: 0.6rem;
  font-weight: 400;
  font-style: normal;
  letter-spacing: 0.2px;
  font-family: var(--font-family);
  margin: 0;
  width: 100%;
  text-align: left;
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: var(--border-radius-sm);
  padding: 0;
  flex-shrink: 0;
}

.logout-btn:hover {
  background: var(--state-hover);
  color: var(--text-primary);
  transform: scale(1.1);
}

.logout-btn i {
  font-size: 0.8rem;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
}

</style>
